import { BooleanProperty } from '../BooleanProperty';
import { IconTextProperty } from '../IconTextProperty';
import { TextTextProperty } from '../TextTextProperty';

import { getMetaCategoryCheckValue } from '../PrototypeSection/PagePrototypeMeta.helpers';

export type ReactionPropertyConfig = {
  key: string;
  value: unknown;
  dependProperty?: { key: string; value: unknown } | null;
  Component?: typeof TextTextProperty | typeof IconTextProperty | typeof BooleanProperty;
  useTransitionDirection?: boolean;
};

export const getReactionsPropertyIndex = (properties: Array<{ key: string }>) =>
  properties.findIndex((property) => property.key === 'reactions');

export const getReactionsPropertyPrefix = ({
  namePrefix,
  properties,
}: {
  namePrefix: string;
  properties: Array<{ key: string }>;
}) => {
  const reactionsPropertyIndex = getReactionsPropertyIndex(properties);

  if (reactionsPropertyIndex === -1) {
    return null;
  }

  return `${namePrefix}.properties[${reactionsPropertyIndex}]`;
};

export const getReactionCheckPrefix = ({
  reactionsPropertyPrefix,
  interactionIndex,
  propertyKey,
}: {
  reactionsPropertyPrefix: string;
  interactionIndex: number;
  propertyKey: string;
}) =>
  `${reactionsPropertyPrefix}.value[${interactionIndex}].checks.${propertyKey}`;

const getNavigationType = (interaction: Record<string, any>) => {
  let navigationType = interaction.action ? interaction.action.type : null;

  if (navigationType === 'NODE') {
    navigationType = interaction.action.navigation;
  }

  return navigationType;
};

const getTransitionEasingData = (interaction: Record<string, any>) => {
  const transitionEasingType =
    interaction.action?.transition?.easing?.type ?? null;

  if (transitionEasingType === 'CUSTOM_CUBIC_BEZIER') {
    return interaction.action.transition.easing.easingFunctionCubicBezier;
  }

  if (transitionEasingType === 'CUSTOM_SPRING') {
    return interaction.action.transition.easing.easingFunctionSpring;
  }

  return null;
};

export const getInteractionPropertyConfigs = (
  interaction: Record<string, any>,
): ReactionPropertyConfig[] => {
  const triggerType = interaction.trigger?.type ?? null;
  const triggerDelay = interaction.trigger?.delay ?? null;
  const triggerKeyCodes = interaction.trigger?.keyCodes ?? null;
  const navigationType = getNavigationType(interaction);
  const transitionType = interaction.action?.transition?.type ?? null;
  const transitionDirection = interaction.action?.transition?.direction ?? null;
  const transitionMatchLayers =
    interaction.action?.transition?.matchLayers ?? null;
  const transitionEasingType =
    interaction.action?.transition?.easing?.type ?? null;
  const transitionEasingDuration =
    interaction.action?.transition?.duration ?? null;
  const transitionEasingData = getTransitionEasingData(interaction);

  const configs: ReactionPropertyConfig[] = [
    {
      key: 'interactionTriggerType',
      value: triggerType,
      Component: TextTextProperty as ReactionPropertyConfig['Component'],
    },
  ];

  if (
    triggerType === 'MOUSE_ENTER' ||
    triggerType === 'MOUSE_LEAVE' ||
    triggerType === 'MOUSE_UP' ||
    triggerType === 'MOUSE_DOWN'
  ) {
    configs.push({
      key: 'interactionTriggerDelay',
      value: triggerDelay,
      Component: TextTextProperty as ReactionPropertyConfig['Component'],
    });
  }

  if (triggerType === 'ON_KEY_DOWN') {
    configs.push({
      key: 'interactionTriggerKeyCodes',
      value: triggerKeyCodes,
      Component: TextTextProperty as ReactionPropertyConfig['Component'],
    });
  }

  if (!interaction.action) {
    return configs;
  }

  configs.push({
    key: 'interactionNavigationType',
    value: navigationType,
    dependProperty: {
      key: 'interactionDestination',
      value: interaction.action.destination || null,
    },
    Component: IconTextProperty as ReactionPropertyConfig['Component'],
  });

  if (navigationType === 'URL') {
    configs.push({
      key: 'interactionNavigationLinkUrl',
      value: interaction.action.url,
      Component: TextTextProperty as ReactionPropertyConfig['Component'],
    });

    return configs;
  }

  if (
    navigationType !== 'BACK' &&
    navigationType !== 'CLOSE' &&
    navigationType !== 'URL'
  ) {
    configs.push({
      key: 'interactionTransition',
      value: transitionType,
      Component: TextTextProperty as ReactionPropertyConfig['Component'],
    });

    if (
      transitionType === 'MOVE_IN' ||
      transitionType === 'MOVE_OUT' ||
      transitionType === 'PUSH' ||
      transitionType === 'SLIDE_IN' ||
      transitionType === 'SLIDE_OUT'
    ) {
      configs.push({
        key: 'interactionTransitionDirection',
        value: transitionDirection,
        useTransitionDirection: true,
      });
      configs.push({
        key: 'interactionTransitionMatchLayers',
        value: transitionMatchLayers,
        Component: BooleanProperty as ReactionPropertyConfig['Component'],
      });
    }

    if (transitionType) {
      configs.push(
        {
          key: 'interactionTransitionEasing',
          value: transitionEasingType,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
        {
          key: 'interactionTransitionDuration',
          value: transitionEasingDuration,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
      );
    }

    if (transitionEasingType === 'CUSTOM_CUBIC_BEZIER' && transitionEasingData) {
      configs.push(
        {
          key: 'interactionTransitionX1',
          value: transitionEasingData.x1,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
        {
          key: 'interactionTransitionX2',
          value: transitionEasingData.x2,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
        {
          key: 'interactionTransitionY1',
          value: transitionEasingData.y1,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
        {
          key: 'interactionTransitionY2',
          value: transitionEasingData.y2,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
      );
    }

    if (transitionEasingType === 'CUSTOM_SPRING' && transitionEasingData) {
      configs.push(
        {
          key: 'interactionTransitionStiffness',
          value: transitionEasingData.stiffness,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
        {
          key: 'interactionTransitionDamping',
          value: transitionEasingData.damping,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
        {
          key: 'interactionTransitionMass',
          value: transitionEasingData.mass,
          Component: TextTextProperty as ReactionPropertyConfig['Component'],
        },
      );
    }

    configs.push({
      key: 'interactionPreserveScrollPosition',
      value: interaction.action.preserveScrollPosition || false,
      Component: BooleanProperty as ReactionPropertyConfig['Component'],
    });
  }

  return configs;
};

export const ensureReactionChecks = (reaction: Record<string, unknown>) => {
  const checks =
    reaction.checks && typeof reaction.checks === 'object'
      ? { ...(reaction.checks as Record<string, boolean>) }
      : {};

  getInteractionPropertyConfigs(reaction).forEach((config) => {
    if (checks[config.key] === undefined) {
      checks[config.key] = false;
    }
  });

  return {
    ...reaction,
    checks,
  };
};

export const getReactionCheckValues = (
  reactions: Array<Record<string, unknown>>,
) =>
  reactions.flatMap((interaction) =>
    getInteractionPropertyConfigs(interaction).map((config) =>
      Boolean(
        (interaction.checks as Record<string, boolean> | undefined)?.[
          config.key
        ],
      ),
    ),
  );

export const getReactionsCategoryCheckValue = (
  reactions: Array<Record<string, unknown>>,
) => getMetaCategoryCheckValue(getReactionCheckValues(reactions));

export const mapReactionsWithChecks = ({
  reactions,
  checked,
}: {
  reactions: Array<Record<string, unknown>>;
  checked: boolean;
}) =>
  reactions.map((interaction) => {
    const nextChecks = {
      ...((interaction.checks as Record<string, boolean>) ?? {}),
    };

    getInteractionPropertyConfigs(interaction).forEach((config) => {
      nextChecks[config.key] = checked;
    });

    return {
      ...interaction,
      checks: nextChecks,
    };
  });

export const shouldCheckReactionsProperty = (
  reactions: Array<Record<string, unknown>>,
) => getReactionCheckValues(reactions).some(Boolean);
